import { jsx } from "react/jsx-runtime";
import { useRef, useEffect } from "react";
const DEFAULT_PARTICLE_COLORS = {
  ring1: "#FF7DD9",
  ring2: "#75ECFF",
  ring3: "#75AFFF",
  ring4: "#7575FF"
};
function hexToHue(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const r = (n >> 16 & 255) / 255;
  const g = (n >> 8 & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h;
  if (max === r) h = (g - b) / d % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return h < 0 ? h + 360 : h;
}
const ParticleBlob = ({ level, size, colors }) => {
  const canvasRef = useRef(null);
  const levelRef = useRef(level);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);
  const colorsKey = colors ? `${colors.ring1}|${colors.ring2}|${colors.ring3}|${colors.ring4}` : "default";
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = typeof window !== "undefined" && window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    const cc = colors ?? DEFAULT_PARTICLE_COLORS;
    const RINGS = [
      { count: 44, radius: size * 0.22, hue: hexToHue(cc.ring1) },
      { count: 52, radius: size * 0.28, hue: hexToHue(cc.ring2) },
      { count: 40, radius: size * 0.345, hue: hexToHue(cc.ring3) },
      { count: 30, radius: size * 0.4, hue: hexToHue(cc.ring4) }
    ];
    const glowHue = RINGS[1].hue;
    const particles = [];
    RINGS.forEach((ring) => {
      for (let i = 0; i < ring.count; i++) {
        particles.push({
          a: i / ring.count * Math.PI * 2 + Math.random() * 0.12,
          baseR: ring.radius + (Math.random() - 0.5) * 3,
          speed: 25e-4 + Math.random() * 2e-3,
          phase: Math.random() * Math.PI * 2,
          sz: 1.2 + Math.random() * 1.4,
          hue: ring.hue + Math.random() * 25 - 12
        });
      }
    });
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const l = levelRef.current;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.25 + l * size * 0.16);
      grad.addColorStop(0, `hsla(${glowHue}, 95%, 75%, ${0.55 + l * 0.35})`);
      grad.addColorStop(1, `hsla(${glowHue}, 95%, 75%, 0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      for (const p of particles) {
        p.a += p.speed * (1 + l * 1.5);
        const breathe = Math.sin(performance.now() * 2e-3 + p.phase) * 2;
        const rr = p.baseR + breathe + l * 10;
        const x = cx + Math.cos(p.a) * rr;
        const y = cy + Math.sin(p.a) * rr;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 95%, ${65 + l * 15}%, ${0.55 + l * 0.4})`;
        ctx.arc(x, y, p.sz + l * 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [size, colorsKey]);
  return /* @__PURE__ */ jsx("canvas", { ref: canvasRef, style: { width: size, height: size }, "aria-hidden": true });
};
export {
  ParticleBlob
};
//# sourceMappingURL=ParticleBlob.js.map
